import { Resolve, RouterModule, Routes, ActivatedRouteSnapshot } from "@angular/router";
import { LoggedInGuard } from "../auth/auth.guards";
import { Injectable, NgModule } from "@angular/core";
import { AssististCategoriesListComponent } from "./categories-list/assistist-categories-list.component";
import { PermissionService } from "../permission.service";
import { 
  AssististCategoriesListGuard, 
  AssististCategoriesWriteGuard, 
  AssististCategoriesDetailGuard,
  AssististDocumentsListGuard,
  AssististDocumentsWriteGuard,
  AssististDocumentsDetailGuard
} from "./assistist.guards";
import { AssististService } from "./assistist.service";
import { SessionService } from "../session.service";
import { take } from "rxjs/operators";
import { IntegrationsService } from "../integrations/integrations.service";
import { assististDocumentsListComponent } from "./documents-list/documents-list.component";
import { assististDocumentsEditComponent } from "./documents-edit/documents-edit.component";
import { assististCategoriesEditComponent } from "./categories-edit/categories-edit.component";
import { DocumentsPreviewComponent } from "./documents-preview/documents-preview.component";
import { IntegrationUsageReportComponent } from "./usage-report/integration-usage-report.component";
import { FeedbackListComponent } from "./feedback-list/feedback-list.component";
import { ThreadListComponent } from "./thread-list/thread-list.component";
import { ThreadDetailComponent } from "./thread-detail/thread-detail.component";
import { CanDeactivateGuard } from "../shared/guards/can-deactivate.guard";
import { AssististIntegrationPreviewComponent } from "./integration-preview/integration-preview.component";

// TODO: Import assistist components once they are created
// import { AssististDocumentsListComponent } from "./documents-list/assistist-documents-list.component";

@Injectable({ providedIn: "root" })
class AssististCategoriesListPermissionsResolve implements Resolve<any> {
  constructor(private PermissionService: PermissionService) {}

  resolve() {
    // Wait for permissions to be loaded, then check permissions
    return new Promise((resolve) => {
      if (this.PermissionService.permissionsLoaded) {
        // Permissions already loaded, proceed immediately
        this.checkPermissions().then(resolve);
      } else {
        // Wait for permissions to be loaded
        this.PermissionService.permissionsLoadedObs.subscribe((loaded) => {
          if (loaded) {
            this.checkPermissions().then(resolve);
          }
        });
      }
    });
  }
  
  private checkPermissions() {
    return Promise.all([
      this.PermissionService.categories.assististCategories?.hasWrite() || Promise.resolve(false),
      this.PermissionService.categories.assististCategories?.hasDetail() || Promise.resolve(false),
      this.PermissionService.categories.assististCategories?.hasList() || Promise.resolve(false),
      this.PermissionService.categories.assististCategories?.hasRemove() || Promise.resolve(false),
    ]).then(
      ([
        hasCategoriesWrite,
        hasCategoriesDetail,
        hasCategoriesList,
        hasCategoriesRemove,
      ]) => {
        return {
          hasCategoriesWrite,
          hasCategoriesDetail,
          hasCategoriesList,
          hasCategoriesRemove,
        };
      },
    );
  }
}

@Injectable({ providedIn: "root" })
class AssististDocumentsListPermissionsResolve implements Resolve<any> {
  constructor(private PermissionService: PermissionService) {}

  resolve() {
    // Wait for permissions to be loaded, then check permissions
    return new Promise((resolve) => {
      if (this.PermissionService.permissionsLoaded) {
        // Permissions already loaded, proceed immediately
        this.checkPermissions().then(resolve);
      } else {
        // Wait for permissions to be loaded
        this.PermissionService.permissionsLoadedObs.subscribe((loaded) => {
          if (loaded) {
            this.checkPermissions().then(resolve);
          }
        });
      }
    });
  }
  
  private checkPermissions() {
    return Promise.all([
      this.PermissionService.categories.assististDocuments?.hasWrite() || Promise.resolve(false),
      this.PermissionService.categories.assististDocuments?.hasDetail() || Promise.resolve(false),
      this.PermissionService.categories.assististDocuments?.hasList() || Promise.resolve(false),
    ]).then(
      ([
        hasDocumentsWrite,
        hasDocumentsDetail,
        hasDocumentsList,
      ]) => {

        return {
          hasDocumentsWrite,
          hasDocumentsDetail,
          hasDocumentsList,
        };
      },
    );
  }
}

@Injectable({ providedIn: "root" })
export class AssististCategoryResolve implements Resolve<any> {
  constructor(
    private svc: AssististService,
    private session: SessionService,
  ) {}

  resolve(route: ActivatedRouteSnapshot) {
    const accountId = this.session.currentAccountValue!.accountID;
    const integrationId = +route.params.integrationId;
    const catId = +route.params.id;
    return this.svc.getDocumentCategory(accountId, integrationId, catId).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class AssististIntegrationsResolve implements Resolve<any> {
  constructor(
    private IntegrationsService: IntegrationsService,
    private SessionService: SessionService,
  ) {}

  resolve(route: ActivatedRouteSnapshot) {
    const featureType = route.params.featureType; // 'tripist' | 'explorist' | 'aura'
    const providerName = featureType === 'tripist'
      ? 'tripist_assistant'
      : featureType === 'aura'
        ? 'aura_assistant'
        : 'explorist_assistant';
    
    return this.IntegrationsService.getIntegrations(
      this.SessionService.currentAccountValue!.accountID,
      this.SessionService.currentAppValue!.id,
      false,
      providerName,
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class AssististDocumentCategoriesResolve implements Resolve<any> {
  constructor(
    private svc: AssististService,
    private session: SessionService,
  ) {}

  resolve(route: ActivatedRouteSnapshot) {
    const accountId = this.session.currentAccountValue!.accountID;
    const integrationId = +route.params.integrationId;
    return this.svc.listDocumentCategories(accountId, integrationId, {}).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class AssististIntegrationResolve implements Resolve<any> {
  constructor(
    private IntegrationsService: IntegrationsService,
    private SessionService: SessionService,
  ) {}

  resolve(route: ActivatedRouteSnapshot) {
    return this.IntegrationsService.getIntegration(
      this.SessionService.currentAccountValue!.accountID,
      route.params.integrationId,
      this.SessionService.currentAppValue!.id,
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class AssististDocumentEditPermissionsResolve implements Resolve<any> {
  constructor(private PermissionService: PermissionService) {}

  resolve() {
    // Wait for permissions to be loaded, then check permissions
    return new Promise((resolve) => {
      if (this.PermissionService.permissionsLoaded) {
        // Permissions already loaded, proceed immediately
        this.checkPermissions().then(resolve);
      } else {
        // Wait for permissions to be loaded
        this.PermissionService.permissionsLoadedObs.subscribe((loaded) => {
          if (loaded) {
            this.checkPermissions().then(resolve);
          }
        });
      }
    });
  }
  
  private checkPermissions() {
    return Promise.all([
      this.PermissionService.categories.assististDocuments?.hasWrite() || Promise.resolve(false),
      this.PermissionService.categories.assististDocuments?.hasDetail() || Promise.resolve(false),
    ]).then(
      ([
        hasDocumentsWrite,
        hasDocumentsDetail,
      ]) => {
        return {
          hasDocumentsWrite,
          hasDocumentsDetail,
        };
      },
    );
  }
}

@Injectable({ providedIn: "root" })
export class AssististCategoryEditPermissionsResolve implements Resolve<any> {
  constructor(private PermissionService: PermissionService) {}

  resolve() {
    // Wait for permissions to be loaded, then check permissions
    return new Promise((resolve) => {
      if (this.PermissionService.permissionsLoaded) {
        // Permissions already loaded, proceed immediately
        this.checkPermissions().then(resolve);
      } else {
        // Wait for permissions to be loaded
        this.PermissionService.permissionsLoadedObs.subscribe((loaded) => {
          if (loaded) {
            this.checkPermissions().then(resolve);
          }
        });
      }
    });
  }
  
  private checkPermissions() {
    return Promise.all([
      this.PermissionService.categories.assististCategories?.hasWrite() || Promise.resolve(false),
      this.PermissionService.categories.assististCategories?.hasDetail() || Promise.resolve(false),
    ]).then(
      ([
        hasCategoriesWrite,
        hasCategoriesDetail,
      ]) => {
        return {
          hasCategoriesWrite,
          hasCategoriesDetail,
        };
      },
    );
  }
}

const routes: Routes = [
  {
    path: ":featureType/:integrationId", // featureType is 'tripist' | 'explorist' | 'aura'
    canActivate: [LoggedInGuard],
    runGuardsAndResolvers: "always",
    data: { sidebar: "assistist" },
    children: [
      {
        path: "categories",
        children: [
          {
            path: "list",
            component: AssististCategoriesListComponent,
            resolve: {
              permissions: AssististCategoriesListPermissionsResolve,
              integrations: AssististIntegrationsResolve,
            },
            data: { title: "Categories" },
            canActivate: [AssististCategoriesListGuard],
            runGuardsAndResolvers: "always",
          },
          {
            path: "create",
            component: assististCategoriesEditComponent,
            resolve: {
              permissions: AssististCategoryEditPermissionsResolve,
              integrations: AssististIntegrationsResolve,
            },
            data: { title: "Create Category" },
            canActivate: [AssististCategoriesWriteGuard],
            canDeactivate: [CanDeactivateGuard],
            runGuardsAndResolvers: "always",
          },
          {
            path: ":id",
            component: assististCategoriesEditComponent,
            resolve: {
              permissions: AssististCategoryEditPermissionsResolve,
              integrations: AssististIntegrationsResolve,
              category: AssististCategoryResolve,
            },
            data: { title: "Edit Category" },
            canActivate: [AssististCategoriesDetailGuard],
            canDeactivate: [CanDeactivateGuard],
            runGuardsAndResolvers: "always",
          },
        ],
      },
      {
        path: "documents",
        children: [
          {
            path: "list",
            component: assististDocumentsListComponent,
            resolve: {
              permissions: AssististDocumentsListPermissionsResolve,
              integrations: AssististIntegrationsResolve,
            },
            data: { title: "Documents" },
            canActivate: [AssististDocumentsListGuard],
            runGuardsAndResolvers: "always",
          },
          {
            path: "create",
            component: assististDocumentsEditComponent,
            resolve: {
              permissions: AssististDocumentEditPermissionsResolve,
              integrations: AssististIntegrationsResolve,
              categories: AssististDocumentCategoriesResolve,
            },
            data: { title: "Create Document" },
            canActivate: [AssististDocumentsWriteGuard],
            canDeactivate: [CanDeactivateGuard],
            runGuardsAndResolvers: "always",
          },
          {
            path: "preview",
            component: DocumentsPreviewComponent,
            resolve: {
              permissions: AssististDocumentsListPermissionsResolve,
              integrations: AssististIntegrationsResolve,
            },
            data: { title: "Documents Preview" },
            canActivate: [AssististDocumentsListGuard],
            runGuardsAndResolvers: "always",
          },
          {
            path: ":id",
            component: assististDocumentsEditComponent,
            resolve: {
              permissions: AssististDocumentEditPermissionsResolve,
              integrations: AssististIntegrationsResolve,
              categories: AssististDocumentCategoriesResolve,
            },
            data: { title: "Edit Document" },
            canActivate: [AssististDocumentsDetailGuard],
            canDeactivate: [CanDeactivateGuard],
            runGuardsAndResolvers: "always",
          },
        ],
      },
      {
        path: "preview",
        component: AssististIntegrationPreviewComponent,
        resolve: {
          permissions: AssististDocumentsListPermissionsResolve,
          integrations: AssististIntegrationsResolve,
          integration: AssististIntegrationResolve,
        },
        data: { title: "Preview Integration" },
        canActivate: [AssististDocumentsListGuard],
        runGuardsAndResolvers: "always",
      },
      {
        path: "usage-report",
        component: IntegrationUsageReportComponent,
        resolve: {
          permissions: AssististDocumentsListPermissionsResolve,
          integrations: AssististIntegrationsResolve,
          integration: AssististIntegrationResolve,
        },
        data: { title: "Usage Report" },
        canActivate: [AssististDocumentsListGuard],
        runGuardsAndResolvers: "always",
      },
      {
        path: "feedback",
        children: [
          {
            path: "list",
            component: FeedbackListComponent,
            resolve: {
              permissions: AssististDocumentsListPermissionsResolve,
              integrations: AssististIntegrationsResolve,
              integration: AssististIntegrationResolve,
            },
            data: { title: "Feedback" },
            canActivate: [AssististDocumentsListGuard],
            runGuardsAndResolvers: "always",
          },
        ],
      },
      {
        path: "threads",
        children: [
          {
            path: "list",
            component: ThreadListComponent,
            resolve: {
              permissions: AssististDocumentsListPermissionsResolve,
              integrations: AssististIntegrationsResolve,
              integration: AssististIntegrationResolve,
            },
            data: { title: "Threads" },
            canActivate: [AssististDocumentsListGuard],
            runGuardsAndResolvers: "always",
          },
        ],
      },
      {
        path: "thread",
        children: [
          {
            path: "detail/:threadId",
            component: ThreadDetailComponent,
            resolve: {
              permissions: AssististDocumentsListPermissionsResolve,
              integrations: AssististIntegrationsResolve,
              integration: AssististIntegrationResolve,
            },
            data: { title: "Thread Detail" },
            canActivate: [AssististDocumentsListGuard],
            runGuardsAndResolvers: "always",
          },
        ],
      },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class AssististRoutingModule {}

const assististModuleLazyLoadRoutes: Routes = [
  {
    path: "assistist",
    loadChildren: () => import("./assistist.module").then((m) => m.AssististModule),
  },
];

export { assististModuleLazyLoadRoutes }; 